import { IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { firstVisitFlagsStore } from "../../domains/user/stores/firstVisitFlagsStore";
import { isScreenshotSession } from "../../domains/app/screenshotSession";
import { isAndroidApp } from "../../tools/android";
import { isIOSApp } from "../../tools/ios";
import { displayToast } from "../app/AppToast";
import AppModal from "../modals/AppModal";
import { Button } from "../ui/Button";
import { InlineButton } from "../ui/InlineButton";

export default function DownloadAppModal() {
  const { t } = useTranslation();
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const { seenDownloadAppModal } = firstVisitFlagsStore.get();
    const isMobileBrowser = /iPad|iPhone|iPod|Android/.test(
      navigator.userAgent
    );
    if (
      !seenDownloadAppModal &&
      !isIOSApp() &&
      !isAndroidApp() &&
      isMobileBrowser &&
      !isScreenshotSession()
    ) {
      setVisible(true);
      firstVisitFlagsStore.set((prev) => ({
        ...prev,
        seenDownloadAppModal: true,
      }));
    } else {
      handleClose();
    }
  }, []);

  function handleConfirm() {
    const windowWithMSStream = globalThis as unknown as Window & {
      MSStream?: unknown;
    };
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      windowWithMSStream.MSStream === undefined;
    const isAndroid =
      /Android/.test(navigator.userAgent) &&
      windowWithMSStream.MSStream === undefined;

    if (isIOS)
      globalThis.location.href =
        "https://apps.apple.com/fr/app/juisci/id1588725828";
    else if (isAndroid)
      globalThis.location.href =
        "https://play.google.com/store/apps/details?id=com.juisci.app&hl=us&gl=US";
    else displayToast(t("Something went wrong. Please try again."), "error");

    handleClose();
  }

  function handleClose() {
    setVisible(false);
  }

  return (
    <AppModal
      visible={isVisible}
      modalStyle={{
        backgroundColor: "var(--color-ds-white)",
        borderRadius: 24,
        padding: "12px 16px 24px 16px",
        gap: 24,
      }}
    >
      <div className="flex w-full items-start justify-end">
        <InlineButton
          aria-label={t("Close")}
          onClick={(e) => {
            e.preventDefault();
            handleClose();
          }}
        >
          <IconX />
        </InlineButton>
      </div>

      <img
        src="/juisci-app-icon.png"
        alt=""
        className="size-18.75 shrink-0 self-center object-cover"
      />

      <div className="flex w-full flex-col items-center gap-6 text-center text-ds-black">
        <h2 className="font-dm-sans text-[30px]/8 font-semibold tracking-[-1px]">
          {t("You're all set.")}
          <br />
          {t("Now get the full experience.")}
        </h2>
        <p className="font-dm-sans text-[15px]/6 font-normal tracking-[-0.15px] px-4">
          {t(
            "The Juisci app gives you push notifications for your daily briefings, offline article reading, and one-tap access without opening a browser."
          )}
        </p>
      </div>

      <div className="flex w-full flex-col items-stretch gap-2">
        <Button
          variant="ghost"
          size="lg"
          data-testid="dismiss-modal"
          onClick={(e) => {
            e.preventDefault();
            handleClose();
          }}
        >
          {t("Continue in browser")}
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={(e) => {
            e.preventDefault();
            handleConfirm();
          }}
        >
          {t("Download the app")}
        </Button>
      </div>
    </AppModal>
  );
}
